import {
  useFeatureHidden,
  useFeaturesLoaded,
  useHasFeature,
} from '@/components/feature-gate'

/** Provider keys as the API stores them (`external_connections.provider`). */
export type AccountingProvider = 'xero' | 'zoho'

export interface AccountingProviderInfo {
  /** Name as users know it. */
  label: string
  /** Path segment in SPA routes and API endpoints. */
  slug: string
  /** Plan feature that unlocks the integration. */
  feature: string
  /** What the provider calls the business a Space connects to. */
  organisation: string
}

export const ACCOUNTING_PROVIDERS: Record<
  AccountingProvider,
  AccountingProviderInfo
> = {
  xero: {
    label: 'Xero',
    slug: 'xero',
    feature: 'integrations.xero',
    organisation: 'Xero organisation',
  },
  zoho: {
    label: 'Zoho Books',
    slug: 'zoho-books',
    feature: 'integrations.zoho-books',
    organisation: 'Zoho Books organisation',
  },
}

export function accountingProvider(
  provider?: string | null,
): AccountingProviderInfo | undefined {
  return ACCOUNTING_PROVIDERS[provider as AccountingProvider]
}

/** "Xero", "Zoho Books", or a neutral phrase when the provider is not known yet. */
export function providerLabel(provider?: string | null): string {
  return accountingProvider(provider)?.label ?? 'your accounting software'
}

/**
 * Whether the Space's plan includes an accounting integration (any provider) —
 * what the shared mapping and Bank Feed screens need. `loaded` is false while
 * the plan catalogue is still loading (useHasFeature is optimistic until then).
 * `hidden`: no provider is available and the plan says not to show them at
 * all (Basic) — every integration entry point disappears.
 */
export function useAccountingIntegrationFeature(): {
  hasFeature: boolean
  loaded: boolean
  hidden: boolean
} {
  const hasXero = useHasFeature(ACCOUNTING_PROVIDERS.xero.feature)
  const hasZoho = useHasFeature(ACCOUNTING_PROVIDERS.zoho.feature)
  const xeroHidden = useFeatureHidden(ACCOUNTING_PROVIDERS.xero.feature)
  const zohoHidden = useFeatureHidden(ACCOUNTING_PROVIDERS.zoho.feature)
  const loaded = useFeaturesLoaded()

  return {
    hasFeature: hasXero || hasZoho,
    loaded,
    hidden: xeroHidden && zohoHidden,
  }
}
